import { useEffect, useState } from "react";
import { Languages, Plus, Check, Pencil, Archive, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { langName, langNative, langFlag, pairAllowed, isSerbian, langDisplayName } from "@/lib/languages";
import LevelDialog from "@/components/LevelDialog";
import LanguageSelect from "@/components/LanguageSelect";
import SerbianScriptSelect from "@/components/SerbianScriptSelect";
import { useI18n } from "@/context/I18nContext";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function LanguageProfiles() {
  const { user, refresh } = useAuth();
  const { t, locale } = useI18n();
  const name = (c) => langDisplayName(c, locale) || langName(c);
  const [data, setData] = useState(null);
  const [levelFor, setLevelFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ native_lang: "en", target_lang: "fr", level: "A1", name: "", target_script: "cyrillic" });

  const canUse = user?.platform_role === "owner" || user?.is_admin;

  const load = () => api.get("/profiles").then((r) => setData(r.data)).catch(() => setData(null));
  // Every learner may view their profiles and change level; other management stays owner/admin-only.
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  if (!data) return null;

  const activate = async (id) => {
    setBusy(true);
    try { await api.post(`/profiles/${id}/activate`); await refresh(); await load(); toast.success("Learning profile switched"); }
    catch { toast.error("Could not switch"); } finally { setBusy(false); }
  };
  const create = async () => {
    if (form.native_lang === form.target_lang) {
      toast.error("Pick two different languages to speak and learn.");
      return;
    }
    const showScript = isSerbian(form.target_lang) || isSerbian(form.native_lang);
    const body = { ...form };
    if (!showScript) delete body.target_script;
    setBusy(true);
    try {
      const r = await api.post("/profiles", body);
      await api.post(`/profiles/${r.data.profile_id}/activate`);
      await refresh(); await load();
      setAdding(false); setForm({ native_lang: "en", target_lang: "fr", level: "A1", name: "", target_script: "cyrillic" });
      toast.success("Profile created & activated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); } finally { setBusy(false); }
  };
  const pickNative = (v) => setForm((f) => ({ ...f, native_lang: v, target_lang: pairAllowed(v, f.target_lang) ? f.target_lang : "en" }));
  const pickTarget = (v) => setForm((f) => ({ ...f, target_lang: v, native_lang: pairAllowed(f.native_lang, v) ? f.native_lang : "en" }));
  const rename = async (p) => {
    const n = window.prompt("Profile name (optional):", p.name || "");
    if (n === null) return;
    try { await api.patch(`/profiles/${p.profile_id}`, { name: n }); load(); } catch { toast.error("Could not rename"); }
  };
  const archive = async (id) => {
    try { await api.post(`/profiles/${id}/archive`); await refresh(); load(); toast.success("Archived"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
  };

  const active = data.profiles.filter((p) => p.status !== "archived");

  return (
    <div className="nb-card p-6 mt-6" data-testid="language-profiles">
      <h3 className="font-cabinet font-bold text-xl flex items-center gap-2"><Languages className="w-5 h-5" /> {t("settings.learningProfiles")}</h3>
      <p className="text-sm text-gray-600 mt-1">{canUse ? t("settings.learningProfilesHelp") : t("lvl.profilesHelp")}</p>

      <div className="space-y-2 mt-4">
        {active.map((p) => {
          const isActive = p.profile_id === data.active_learning_profile_id;
          return (
            <div key={p.profile_id} className={`flex items-center justify-between gap-2 border-2 border-ink rounded-xl px-3 py-2 ${isActive ? "bg-mint/50" : "bg-white"}`} data-testid={`profile-${p.profile_id}`}>
              <div className="min-w-0">
                <div className="font-cabinet font-bold truncate flex items-center gap-1.5">
                  <span>{langFlag(p.target_lang)}</span>
                  <span className="truncate">{p.name || name(p.target_lang)}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1" dir="auto">
                  {name(p.native_lang)} → {name(p.target_lang)}
                  <span className="text-gray-400">· {langNative(p.target_lang)}</span>
                  <span>· <span dir="ltr" data-testid={`profile-level-${p.profile_id}`}>{p.level}</span></span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                <button onClick={() => setLevelFor(p)} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid={`change-level-${p.profile_id}`}>{t("lvl.changeLevel")}</button>
                {canUse && (isActive ? (
                  <span className="nb-tag bg-mint text-xs flex items-center gap-1"><Check className="w-3 h-3" /> {t("settings.active")}</span>
                ) : (
                  <button onClick={() => activate(p.profile_id)} disabled={busy} className="nb-btn-primary py-1.5 px-3 text-xs" data-testid={`activate-${p.profile_id}`}>{t("settings.switch")}</button>
                ))}
                {canUse && <button onClick={() => rename(p)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid={`rename-${p.profile_id}`}><Pencil className="w-3 h-3" /></button>}
                {canUse && active.length > 1 && <button onClick={() => archive(p.profile_id)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid={`archive-${p.profile_id}`}><Archive className="w-3 h-3" /></button>}
              </div>
            </div>
          );
        })}
      </div>

      <LevelDialog open={!!levelFor} profile={levelFor} onClose={() => setLevelFor(null)} onSaved={() => load()} />

      {!canUse ? null : adding ? (
        <div className="nb-card bg-sky p-4 mt-4 space-y-3" data-testid="add-profile-form">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold uppercase text-gray-500">{t("auth.iSpeak")}</label>
              <LanguageSelect role="native" value={form.native_lang} onChange={pickNative} partner={form.target_lang} testid="np-native" /></div>
            <div><label className="text-xs font-bold uppercase text-gray-500">{t("auth.imLearning")}</label>
              <LanguageSelect role="target" value={form.target_lang} onChange={pickTarget} partner={form.native_lang} testid="np-target" /></div>
            <div><label className="text-xs font-bold uppercase text-gray-500">{t("settings.level")}</label>
              <select className="nb-input w-full mt-1" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="np-level">{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
            <div><label className="text-xs font-bold uppercase text-gray-500">{t("settings.nameOptional")}</label>
              <input className="nb-input w-full mt-1" placeholder="e.g. Paris Travel" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="np-name" /></div>
          </div>
          {(isSerbian(form.target_lang) || isSerbian(form.native_lang)) && (
            <SerbianScriptSelect value={form.target_script} onChange={(v) => setForm({ ...form, target_script: v })} testid="np-serbian-script" />
          )}
          <div className="flex gap-2">
            <button onClick={create} disabled={busy} className="nb-btn-primary py-2 px-4 flex-1 text-sm" data-testid="np-create">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("settings.createAndSwitch")}</button>
            <button onClick={() => setAdding(false)} className="nb-btn-outline py-2 px-4 text-sm">{t("common.cancel")}</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="nb-btn-outline mt-4 py-2 px-4 text-sm" data-testid="add-profile-btn"><Plus className="w-4 h-4" /> {t("settings.addProfile")}</button>
      )}
    </div>
  );
}
